import React, { useEffect, useCallback, useState } from 'react';
import {
  func, shape, string, bool,
} from 'prop-types';
import isFunction from 'lodash/fp/isFunction';
import NativeDropdown from '@active/react-ui/NativeDropdown';
import MenuItemList from '@active/react-ui/MenuItemList';
import MenuItem from '@active/react-ui/MenuItem';
import { withI18n } from '@active/react-utils/i18n/LocaleContext';
import compose from 'lodash/fp/compose';
import compact from 'lodash/fp/compact';
import first from 'lodash/fp/first';
import omit from 'lodash/fp/omit';
import {
  monthData, yearData, currentYear, currentMonth,
} from './dateDatas';
import './DataSelect.scss';

const DateSelect = ({
  i18n,
  value,
  onChange,
  onBlur,
  required,
  registerValidate,
  ...rest
}) => {
  const [ monthTouched, setMonthTouched ] = useState(false);
  const [ yearTouched, setYearTouched ] = useState(false);
  const { month: monthValue, year: yearValue } = value;

  const onMonthBlur = useCallback(() => {
    setMonthTouched(true);
  }, [ monthTouched ]);

  const onYearBlur = useCallback(() => {
    setYearTouched(true);
  }, [ yearTouched ]);

  useEffect(() => {
    const allTouched = monthTouched && yearTouched;
    const editTouched = (monthTouched && yearValue) || (yearTouched && monthValue);
    (allTouched || editTouched) && onBlur();
  }, [ monthTouched, yearTouched ]);

  const handleMonthChange = useCallback((date) => {
    onChange({
      ...value,
      month: date,
    });
  }, [ value, onChange ]);

  const handleYearChange = useCallback((date) => {
    onChange({
      ...value,
      year: date,
    });
  }, [ value, onChange ]);

  const validateDate = ({ year, month }) => {
    if (required) {
      if (!month || !year) {
        return i18n('react-ui.validation.required.specifyField', { fieldName: i18n('checkout.lib.components.common.expiration.date') });
      }
    }
    const inValidateMonth = () => year && `${currentYear}` === `${year}`
    && currentMonth > parseInt(month, 10);
    return !inValidateMonth() ? '' : i18n('checkout.lib.components.error.validate.month.invalidate');
  };

  const defaultValidator = (date) => {
    const { month, year } = date || {};
    const result = [
      validateDate({ month, year }),
    ];
    return compose(first, compact)(result);
  };

  useEffect(() => {
    if (isFunction(registerValidate)) {
      registerValidate(defaultValidator);
    }
  }, [ registerValidate, required, i18n ]);

  return (
    <div className="date-select">
      <NativeDropdown
        placeholder={i18n('checkout.lib.components.newCreditCard.placeholder.month')}
        name="month"
        autoComplete="cc-exp-month"
        value={value.month}
        onChange={handleMonthChange}
        onBlur={onMonthBlur}
        aria-label={i18n('checkout.lib.components.payment.credit.cart.wcag.expireDate.month')}
        { ...omit([ 'name', 'id', 'aria-labelledby' ], rest) }>
        <MenuItemList>
          {monthData.map(item => (<MenuItem value={item.value} key={item.value}>{item.text}</MenuItem>))}
        </MenuItemList>
      </NativeDropdown>
      <span className='date-separator'>/</span>
      <NativeDropdown
        placeholder={i18n('checkout.lib.components.newCreditCard.placeholder.year')}
        name="year"
        autoComplete="cc-exp-year"
        value={value.year}
        onChange={handleYearChange}
        onBlur={onYearBlur}
        aria-label={i18n('checkout.lib.components.payment.credit.cart.wcag.expireDate.year')}
        { ...omit([ 'name', 'id', 'aria-labelledby' ], rest) }>
        <MenuItemList>
          {yearData.map(item => (<MenuItem value={item.value} key={item.value}>{item.text}</MenuItem>))}
        </MenuItemList>
      </NativeDropdown>
    </div>
  );
};

DateSelect.propTypes = {
  i18n: func.isRequired,
  value: shape({
    month: string,
    year: string,
  }),
  onChange: func.isRequired,
  required: bool,
  registerValidate: func.isRequired,
};

DateSelect.defaultProps = {
  value: {},
  required: false,
};
export default withI18n(DateSelect);
